import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Mail } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../components/Tables/APITable";
import Button from "../../components/Button/Button";
import Notification from "../../components/Notification/Notification";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./ApiCatalogueHome.module.scss";

export default function ApiCatalogueHome() {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "error") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     FETCH SANDBOX APIS
  ========================= */
  useEffect(() => {
    const fetchSandboxApis = async () => {
      setLoading(true);
      try {
        const res = await apiGet("catalog", "/apimaster/sandbox");

        if (res?.status === "success" && Array.isArray(res.data)) {
          setApis(
            res.data.map((api) => ({
              id: api.id,
              apiName: api.name || "--",
              description: api.introduction || "--",
            })),
          );
        } else {
          showNotification(res?.message || "Failed to fetch sandbox APIs", "error");
        }
      } catch (error) {
        console.error("Sandbox APIs error", error);
        showNotification("Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchSandboxApis();
  }, []);

  /* =========================
     TABLE CONFIG
  ========================= */
  const columns = [
    { label: "Name", key: "apiName" },
    { label: "Description", key: "description" },
  ];

  /* =========================
     JSX
  ========================= */
  return (
    <div className={`${styles["api-sandbox"]} api-sandbox`}>
      {notification && (
        <div className={styles["api-sandbox__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["api-sandbox__header"]}>
        <div className={styles["api-sandbox__header-left"]}>
          <div className={styles["api-sandbox__icon-wrapper"]}>
            <FlaskConical size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["api-sandbox__title-group"]}>
            <h2 className={styles["api-sandbox__title"]}>Sandbox APIs</h2>
            <p className={styles["api-sandbox__subtitle"]}>
              Explore the APIs available in the sandbox environment.
            </p>
          </div>
        </div>

        <Button
          text="Contact Us"
          icon={Mail}
          onClick={() => navigate("/contact")}
        />
      </div>

      {/* TABLE CARD */}
      <div className={styles["api-sandbox__card"]}>
        <div className={styles["api-sandbox__showing-text"]}>
          Showing {apis.length} {apis.length === 1 ? "API" : "APIs"}
        </div>

        {loading ? (
          <TableSkeleton columns={2} rows={6} />
        ) : (
          <APITable
            columns={columns}
            data={apis}
            renderCell={(row, key) => {
              if (key === "apiName") {
                return <span className={styles["api-sandbox__name"]}>{row.apiName}</span>;
              }
              if (key === "description") {
                return (
                  <span className={styles["api-sandbox__desc"]}>{row.description}</span>
                );
              }
              return row[key];
            }}
          />
        )}
      </div>
    </div>
  );
}
